import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Building2, ArrowUpRight, Eye, Send } from "lucide-react";

/**
 * "Top brand viewers" — reciprocity signal widget.
 *
 * Surfaces brands that clicked through the tracked outreach-email CTA. Powers
 * the "$brand opened your kit 4 times this week" narrative + lets the athlete
 * jump straight to compose a follow-up. Hidden when no attributed views exist
 * (the upgrade path is to keep sending tracked outreach via PITCHD).
 */
function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const diffSec = Math.max(0, Math.round((new Date() - new Date(iso)) / 1000));
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
    return `${Math.round(diffSec / 86400)}d ago`;
  } catch { return ""; }
}


export default function BrandViewersCard() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/dashboard/kit-views/by-brand?limit=5")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoaded(true));
  }, []);

  // Hidden state — render nothing until we know whether to show.
  if (!loaded) return null;
  if (!data || !data.share_enabled || !data.brands || data.brands.length === 0) return null;

  const top = data.brands[0];
  const verb = top.views_7d === 0 ? "viewed your kit"
             : top.views_7d === 1 ? "opened your kit once this week"
             : `opened your kit ${top.views_7d} times this week`;

  return (
    <div className="surface p-6 border-volt/40 bg-volt/[0.02]" data-testid="brand-viewers-card">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow text-volt">Reciprocity signal · 30d</div>
        <Eye className="w-4 h-4 text-volt" />
      </div>
      <h3 className="font-heading text-2xl uppercase tracking-tight leading-tight" data-testid="brand-viewers-headline">
        <span className="text-volt">{top.brand_name}</span> {verb}.
      </h3>
      <p className="text-xs text-ink-muted mt-2">
        Brands clicking your tracked outreach link. Follow up while you're top-of-mind.
      </p>

      <ul className="mt-5 space-y-2.5 divide-y divide-line" data-testid="brand-viewers-list">
        {data.brands.map(b => (
          <li key={b.brand_id} className="flex items-center justify-between gap-3 pt-2.5 first:pt-0"
              data-testid={`brand-viewer-${b.brand_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 border border-line flex items-center justify-center flex-shrink-0">
                <Building2 className="w-3.5 h-3.5 text-ink-muted" />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-sm uppercase tracking-tight truncate">{b.brand_name}</div>
                <div className="text-[10px] text-ink-muted uppercase tracking-widest truncate">
                  {b.category || "—"} · last {fmtRelative(b.last_viewed)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right">
                <div className="font-mono text-base text-volt">{b.views_30d}</div>
                <div className="text-[9px] text-ink-muted uppercase tracking-widest">30d views</div>
              </div>
              <Link to={`/app/outreach?brand=${b.brand_id}`}
                title={`Draft a follow-up to ${b.brand_name}`}
                className="border border-line hover:border-volt p-3 transition-colors"
                data-testid={`brand-viewer-followup-${b.brand_id}`}>
                <Send className="w-4 h-4 text-volt" />
              </Link>
            </div>
          </li>
        ))}
      </ul>

      <Link to="/app/outreach"
        className="text-xs uppercase tracking-widest text-volt hover:text-white inline-flex items-center gap-1.5 mt-5"
        data-testid="brand-viewers-cta">
        Open outreach <ArrowUpRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
